import React from "react"

const VideoUploadStatus = ({ status, videoReview, uploadStarted, uploadProgress }) => {
  const getStatusIcon = () => {
    switch (status) {
      case "uploading":
        return "icon-upload"
      case "processing":
        return "icon-settings"
      case "completed":
        return "icon-done"
      case "approved":
        return "icon-done"
      case "rejected":
        return "icon-close"
      case "failed":
        return "icon-warning"
      default:
        return "icon-info"
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case "uploading":
        return "text-blue-600 bg-blue-100"
      case "processing":
        return "text-blue-600 bg-blue-100"
      case "completed":
        return "text-green-600 bg-green-100"
      case "approved":
        return "text-green-600 bg-green-100"
      case "rejected":
        return "text-red-600 bg-red-100"
      case "failed":
        return "text-red-600 bg-red-100"
      default:
        return "text-gray-600 bg-gray-100"
    }
  }

  const getStatusMessage = () => {
    switch (status) {
      case "uploading":
        return {
          title: "Uploading Video",
          description: "Your video is being uploaded to our secure servers. Please wait...",
          showProgress: true
        }
      case "processing":
        return {
          title: "Processing Video",
          description:
            "Your video is being optimized and prepared for review. This may take a few minutes. Please wait, don't close this page...",
          showProgress: false
        }
      case "completed":
        return {
          title: "Video Uploaded Successfully",
          description: "Your video has been uploaded and is ready for review by your healthcare provider.",
          showProgress: false
        }
      case "approved":
        return {
          title: "Video Approved",
          description: "Great! Your video has been reviewed and approved by your healthcare provider.",
          showProgress: false
        }
      case "rejected":
        return {
          title: "Video Rejected",
          description:
            "Your video has been reviewed but needs to be resubmitted. Please contact your healthcare provider for details.",
          showProgress: false
        }
      case "failed":
        return {
          title: "Upload Failed",
          description: "There was an error processing your video. Please try uploading again or contact support.",
          showProgress: false
        }
      default:
        return {
          title: "Uploading Video",
          description: "Your video is being uploaded to our secure servers. Please wait, don't close this page...",
          showProgress: true
        }
    }
  }

  const statusInfo = getStatusMessage()
  const statusColorClass = getStatusColor()
  const statusIcon = getStatusIcon()

  return (
    <div className="py-8 text-center">
      {/* Status Icon */}
      <div className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full ${statusColorClass}`}>
        <i className={`${statusIcon} text-3xl`} />
      </div>

      {/* Status Title */}
      <h3 className="mb-3 text-xl font-semibold text-gray-900">{statusInfo.title}</h3>

      {/* Status Description */}
      <p className="mx-auto mb-6 max-w-md leading-relaxed text-gray-600">{statusInfo.description}</p>

      {/* Progress Bar for Uploading */}
      {statusInfo.showProgress && uploadStarted && (
        <div className="mb-6">
          <div className="mb-2 h-3 w-full rounded-full bg-gray-200">
            <div
              className="h-3 rounded-full bg-blue-600 transition-all duration-300 ease-out"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="text-sm text-gray-500">{uploadProgress}% complete</p>
        </div>
      )}

      {/* Processing Animation */}
      {status === "processing" && (
        <div className="mb-6">
          <div className="flex items-center justify-center space-x-1">
            <div className="h-2 w-2 animate-bounce rounded-full bg-blue-600" style={{ animationDelay: "0ms" }}></div>
            <div className="h-2 w-2 animate-bounce rounded-full bg-blue-600" style={{ animationDelay: "150ms" }}></div>
            <div className="h-2 w-2 animate-bounce rounded-full bg-blue-600" style={{ animationDelay: "300ms" }}></div>
          </div>
          <p className="mt-2 text-sm text-blue-600">Processing your video...</p>
        </div>
      )}

      {/* Video Information */}
      {videoReview.originalFilename && videoReview.originalFilename !== "pending_upload" && (
        <div className="mt-6 rounded-lg bg-gray-50 p-4">
          <h4 className="mb-2 font-semibold text-gray-700">Video Details</h4>
          <div className="space-y-1 text-sm text-gray-600">
            <p>
              <strong>Filename:</strong> {videoReview.originalFilename}
            </p>
            {videoReview.fileSizeMb > 0 && (
              <p>
                <strong>Size:</strong> {videoReview.fileSizeMb} MB
              </p>
            )}
            <p>
              <strong>Status:</strong>
              <span
                className={`ml-2 rounded-full px-2 py-1 text-xs font-medium ${
                  status === "uploading"
                    ? "bg-blue-100 text-blue-800"
                    : status === "processing"
                    ? "bg-blue-100 text-blue-800"
                    : status === "completed"
                    ? "bg-green-100 text-green-800"
                    : status === "approved"
                    ? "bg-green-100 text-green-800"
                    : status === "rejected"
                    ? "bg-red-100 text-red-800"
                    : status === "failed"
                    ? "bg-red-100 text-red-800"
                    : "bg-gray-100 text-gray-800"
                }`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Additional Actions */}
      {status === "failed" && (
        <div className="mt-6">
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-red-600 px-6 py-2 font-semibold text-white transition-colors hover:bg-red-700">
            Try Again
          </button>
        </div>
      )}

      {(status === "completed" || status === "approved") && (
        <div className="border-green-200 bg-green-50 mt-6 rounded-lg border p-4">
          <div className="flex items-center justify-center text-green-800">
            <i className="icon-done mr-2" />
            <span className="font-medium">
              {status === "approved"
                ? "Your video has been approved and is now available to your healthcare provider."
                : "Your video is ready for review by your healthcare provider."}
            </span>
          </div>
        </div>
      )}

      {status === "rejected" && (
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="text-center text-red-800">
            <i className="icon-info mb-2 block text-xl" />
            <p className="mb-2 font-medium">Video Review Required</p>
            <p className="text-sm">
              Please contact your healthcare provider to understand what changes are needed and to receive a new upload
              link if necessary.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default VideoUploadStatus
